import { Schedule } from "@aura-frontend/omnibus-az-client";
import { Collapsible, Skeleton } from "@win-reality/react-ui";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import BackButton from "@/components/layout/BackButton";
import { useSingleComplex } from "@/hooks/useComplexes";

import { useScheduledEventsForMonth } from "../SingleField/FieldSchedule/FieldSchedule.queries";
import { FieldCard } from "./FieldCard";

const UPCOMING_WINDOW_S = 3 * 60 * 60;

function SingleComplex() {
  const { complexName } = useParams<{ complexName: string }>();

  const { complex, isLoading } = useSingleComplex(complexName);

  const liveFields = (complex?.fields ?? []).filter((f) => f.currentGame);
  const liveFieldUuids = new Set(liveFields.map((f) => f.machineUuid));

  const { events: upcomingEvents, isLoading: isLoadingEvents } =
    useScheduledEventsForMonth({
      fieldIds: complex ? complex.fields.map((f) => f.machineUuid) : [],
      fromNow: true,
    });

  // upcomingEvents is already sorted ascending by schedule_time_s, so the
  // first hit per machine_uuid is that field's soonest future event. Skip
  // fields that have a live currentGame — those belong in Live SmartParks.
  const soonestByField = useMemo(() => {
    const m = new Map<string, Schedule>();
    for (const e of upcomingEvents) {
      if (liveFieldUuids.has(e.machine_uuid)) continue;
      if (!m.has(e.machine_uuid)) m.set(e.machine_uuid, e);
    }
    return m;
    // liveFieldUuids is a fresh Set each render; comparing it as a dep would
    // re-run on every render. Key off the underlying field list instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingEvents, liveFields]);

  const cutoffS = Math.floor(Date.now() / 1000) + UPCOMING_WINDOW_S;
  const upcomingCards = Array.from(soonestByField.values()).filter(
    (e) => e.schedule_time_s <= cutoffS,
  );
  const upcomingFieldUuids = new Set(upcomingCards.map((e) => e.machine_uuid));

  const availableFields = (complex?.fields ?? []).filter(
    (f) => !f.currentGame && !upcomingFieldUuids.has(f.machineUuid),
  );

  return (
    <div className="py-4">
      <div className="mb-4 max-w-130 m-auto px-5 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex justify-start">
          <BackButton />
        </div>

        <div className="text-center text-base">
          {complex?.name ? (
            complex.name
          ) : (
            <Skeleton className="rounded h-6 w-80 m-auto" />
          )}
        </div>

        <div />
      </div>

      <div className="max-w-130 px-1 m-auto">
        <div>
          <Collapsible
            className="bg-transparent"
            label={`Live SmartParks (${liveFields.length})`}
            defaultOpen
            headerClassName="text-sm text-gray-400! justify-start pb-0 mb-0"
          >
            <div className="flex flex-col gap-3 pt-3">
              {isLoading && <Skeleton className="rounded h-50 w-full" />}

              {liveFields.map((field) => (
                <FieldCard key={field.machineUuid} machineField={field} />
              ))}
            </div>
          </Collapsible>
        </div>

        <div>
          <Collapsible
            className="bg-transparent"
            label={`Active Soon SmartParks (${upcomingCards.length})`}
            defaultOpen
            headerClassName="text-sm text-gray-400! justify-start pb-0 mb-0"
          >
            <div className="flex flex-col gap-3 pt-3">
              {isLoading || isLoadingEvents ? (
                <Skeleton className="rounded h-50 w-full" />
              ) : null}
              {upcomingCards.map((event) => {
                const machineField = complex?.fields.find(
                  (f) => f.machineUuid === event.machine_uuid,
                );

                if (!machineField) return null;

                return (
                  <FieldCard
                    key={event.id}
                    machineField={machineField}
                    maybeUpcomingEvent={event}
                  />
                );
              })}
            </div>
          </Collapsible>
        </div>

        <div>
          <Collapsible
            className="bg-transparent"
            label={`Idle SmartParks (${availableFields.length})`}
            defaultOpen
            headerClassName="text-sm text-gray-400! justify-start pb-0 mb-0"
          >
            <div className="flex flex-col gap-3 pt-3">
              {isLoading && <Skeleton className="rounded h-50 w-full" />}
              {availableFields.map((field) => (
                <FieldCard key={field.machineUuid} machineField={field} />
              ))}
            </div>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}

export { SingleComplex };
